import { BaseSharedComponentType } from "types";
import styles from "./index.module.scss";
import clsx from "clsx";
import { useEffect, useState } from "react";

type LoaderSize = "small" | "medium" | "large";

type Props = {
    /**
     * Additional CSS classes for container
     */
    inlineContainerClasses?: string;
    inlineContainerStyles?: React.CSSProperties;
    /**
     * Adds a subtle background color to the loader container
     */
    backgroundColor?: boolean;
    /**
     * Controls the size of the loader
     */
    size?: LoaderSize;
    /**
     * Adds a label for screen readers
     */
    ariaLabel?: string;
    /**
     * Custom color for the loader
     */
    color?: string;
} & BaseSharedComponentType;

const Loader = ({
    inlineStyles,
    inlineClasses,
    inlineContainerClasses,
    inlineContainerStyles,
    backgroundColor,
    size = "medium",
    ariaLabel = "Loading",
    color
}: Props) => {
    const [isReducedMotion, setIsReducedMotion] = useState(false);
    
    // Check for user's motion preference
    useEffect(() => {
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        setIsReducedMotion(prefersReducedMotion);
    }, []);
    
    // Get appropriate size class
    const getSizeClass = () => {
        switch(size) {
            case "small": return styles.loader_small;
            case "large": return styles.loader_large;
            default: return "";
        }
    };
    
    const customStyle = {
        ...inlineStyles,
        ...(color ? { borderColor: `${color} transparent transparent transparent` } : {})
    };
    
    return (
        <div 
            className={clsx(
                styles.container, 
                backgroundColor && styles.container_backgroundColor, 
                inlineContainerClasses
            )}
            style={inlineContainerStyles}
            role="status"
            aria-live="polite"
        >
            <span 
                className={clsx(
                    styles.loader, 
                    getSizeClass(),
                    inlineClasses
                )} 
                style={customStyle}
                aria-label={ariaLabel}
            ></span>
        </div>
    );
};

export {
    Loader
};